'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import classnames from 'classnames';
import { Icon } from '../Icon/Icon.js';
import { useToggle } from '../../index.js';
function isAnchorProps(props) {
    return props.href ? true : false;
}
export const Button = React.forwardRef((_a, ref) => {
    var { className, variant, size, fullWidth, iconName, iconPosition = 'before', inverted, loading, screenReaderText, text, hideText, circle, onClick, disabled, iconSize, fab, fabThreshold = 50 } = _a, rest = __rest(_a, ["className", "variant", "size", "fullWidth", "iconName", "iconPosition", "inverted", "loading", "screenReaderText", "text", "hideText", "circle", "onClick", "disabled", "iconSize", "fab", "fabThreshold"]);
    const [isActive, { toggle }] = useToggle();
    const [scrollPosition, setScrollPosition] = React.useState(0);
    const [fabIsVisible, setFabIsVisible] = React.useState(false);
    const handleClick = (e) => {
        if (onClick) {
            onClick(e);
        }
        if (fab) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
            setFabIsVisible(false);
        }
        toggle();
    };
    const props = rest;
    const componentClassName = classnames('sparky-c-button', className, fab ? `sparky-c-button--fab-${fab}` : null, fab && fabIsVisible === false ? 'sparky-u-is-hidden' : null, {
        'sparky-c-button--primary': variant === 'primary',
        'sparky-c-button--bare': variant === 'bare',
        'sparky-c-button--link': variant === 'link',
        'sparky-c-button--toolbar': variant === 'toolbar',
        'sparky-c-button--active': variant === 'active',
        'sparky-c-button--sm': size === 'sm',
        'sparky-c-button--lg': size === 'lg',
        'sparky-c-button--table-header': variant === 'table-header',
        'sparky-c-button--inverted': inverted === true,
        'sparky-c-button--full-width': fullWidth,
        'sparky-is-loading': loading,
        'sparky-c-button--circle': circle,
    });
    const loadingIndicatorSize = () => {
        if (size === 'sm') {
            return '2xs';
        }
        else if (size === 'lg') {
            return 'sm';
        }
        else if (iconSize) {
            return iconSize;
        }
        else {
            return 'xs';
        }
    };
    React.useEffect(() => {
        if (fab) {
            const handleScroll = () => {
                const position = window.pageYOffset;
                if (scrollPosition != position) {
                    setScrollPosition(position);
                }
                if (position >= fabThreshold) {
                    return setFabIsVisible(true);
                }
                else if (position <= fabThreshold - 1 || position === 0) {
                    return setFabIsVisible(false);
                }
            };
            window.addEventListener('scroll', handleScroll);
        }
    }, [fab, scrollPosition, fabThreshold]);
    const computedIcon = (_jsxs(_Fragment, { children: [loading && (_jsx(Icon, { "aria-hidden": "true", focusable: false, name: "spinner", className: "sparky-c-button__icon", size: loadingIndicatorSize() })), !loading && iconName && (_jsx(Icon, { "aria-hidden": "true", focusable: false, name: iconName, className: "sparky-c-button__icon", size: iconSize }))] }));
    const content = (_jsxs(_Fragment, { children: [iconPosition === 'before' && computedIcon, text && (_jsxs("span", Object.assign({ className: !hideText
                    ? 'sparky-c-button__text'
                    : 'sparky-c-button__text sparky-u-is-vishidden' }, { children: [text, screenReaderText && (_jsx("span", Object.assign({ className: "sparky-u-is-vishidden" }, { children: screenReaderText })))] }))), iconPosition === 'after' && computedIcon] }));
    if (isAnchorProps(props)) {
        const { buttonRef } = props, anchorRest = __rest(props, ["buttonRef"]);
        return (_jsx("a", Object.assign({ ref: buttonRef ? buttonRef : ref, className: componentClassName, tabIndex: disabled ? -1 : undefined, disabled: disabled }, anchorRest, { children: content })));
    }
    const { buttonRef } = props, buttonRest = __rest(props, ["buttonRef"]);
    return (_jsx("button", Object.assign({ ref: buttonRef, className: componentClassName, "aria-pressed": isActive, onClick: handleClick, disabled: disabled, tabIndex: disabled ? -1 : undefined }, buttonRest, { children: content })));
});
Button.displayName = 'Button';
//# sourceMappingURL=Button.js.map